import { useGraphQLQuery } from '@betterment/js-runtime';
import { Banner, Card, Heading, Separator } from '@betterment/samba-react';
import { CardLoader, ErrorBoundary } from '@betterment/shared-components';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { RiskLevelBasedPortfolioStrategy } from './components/RiskLevelBasedPortfolioStrategy';
import { SelfDirectedPortfolioStrategy } from './components/SelfDirectedPortfolioStrategy';
import { PortfolioStrategyCardDocument } from './graphql/portfolioStrategyCard.generated';

export const PortfolioStrategyCardView: React.FC = () => {
  const accountId = useRequiredParam('accountId');
  const { data } = useGraphQLQuery(PortfolioStrategyCardDocument, {
    variables: { id: accountId },
    throwOnError: true,
  });

  if (!data) return <CardLoader />;
  if (
    !data.envelopeAccount ||
    data.envelopeAccount.__typename !== 'ManagedAccount' ||
    !data.envelopeAccount.portfolio
  ) {
    return null;
  }

  const { legacyGoalId, portfolio, hasEverBeenFunded, messages } = data.envelopeAccount;

  return (
    <Card css={{ padding: 'space.300', display: 'grid', gap: 'space.300' }} data-testid="portfolio-strategy-card">
      <Card.Header css={{ display: 'flex', justifyContent: 'space-between' }}>
        <Heading as="h2" variant="title200">
          Portfolio strategy
        </Heading>
      </Card.Header>
      <Separator />
      <Card.Content css={{ display: 'flex', flexDirection: 'column' }}>
        {portfolio.hasPendingPortfolioChangeRequest && (
          <Banner css={{ mb: 'space.300' }}>We're working on your request to modify your investment portfolio.</Banner>
        )}
        {portfolio.__typename === 'RiskLevelBasedPortfolio' && (
          <RiskLevelBasedPortfolioStrategy
            portfolio={portfolio}
            legacyGoalId={legacyGoalId}
            hasEverBeenFunded={hasEverBeenFunded}
            messages={messages}
          />
        )}
        {portfolio.__typename === 'SelfDirectedPortfolio' && (
          <SelfDirectedPortfolioStrategy
            portfolio={portfolio}
            legacyGoalId={legacyGoalId}
            hasEverBeenFunded={hasEverBeenFunded}
            messages={messages}
          />
        )}
      </Card.Content>
    </Card>
  );
};

export const PortfolioStrategyCard: React.FC = () => {
  return (
    <ErrorBoundary fallbackType="none">
      <PortfolioStrategyCardView />
    </ErrorBoundary>
  );
};
